/* eslint-disable max-lines */

import { type FormField, FormFieldType } from '../../common/types/form/form-fields';
import type {
	AdfValue,
	DateTimeValue,
	DateValue,
	LocalTimeValue,
	NumberValue,
	OptionsValue,
	TextValue,
} from '../../common/types/form/form-fields/field-values';
import { getAttachmentValue } from '../attachment/utils';
import {
	convertFormChoice,
	getChoiceValue,
	getLinkedChoices,
	getUserChoiceValue,
} from '../choice/utils';
import type { ApiFieldDataResponse } from '../types/field-data';
import type { ApiFormChoicesResponse } from '../types/form-choices';
import {
	type FormAnswer,
	type FormQuestion,
	FormQuestionType,
	FormStatus,
} from '../types/proforma-form';
import { convertValidation } from '../validation/utils';

// Note: Assets, Attachment and User field types do not have a default value option
export const convertQuestion = (
	question: FormQuestion,
	formAnswer?: FormAnswer,
	formStatus?: FormStatus,
	apiFieldData?: ApiFieldDataResponse,
	apiFormChoices?: ApiFormChoicesResponse,
	shouldApplyDefaultValues?: boolean,
): FormField => {
	const commonFieldProperties = {
		label: question.label,
		...(question.description && { helperMessage: question.description }),
		validation: convertValidation(
			question,
			question.jiraField ? apiFieldData?.[question.jiraField] : undefined,
			apiFormChoices,
		),
	};
	const isFormLockedOrSubmitted =
		formStatus === FormStatus.Locked || formStatus === FormStatus.Submitted;

	const fieldConfiguration = question.jiraField ? apiFieldData?.[question.jiraField] : undefined;
	// In a submitted (or locked) form, for fields that are linked to Jira fields, we don't want to use the linked Jira
	// field's value, but rather from the stored field values in the submitted form itself.
	const fieldData = isFormLockedOrSubmitted ? undefined : fieldConfiguration;

	switch (question.type) {
		case FormQuestionType.Attachment:
			return {
				type: FormFieldType.ATTACHMENT,
				...commonFieldProperties,
				...(formAnswer?.files && { value: getAttachmentValue(formAnswer.files) }),
			};
		case FormQuestionType.ChoiceCascading:
		case FormQuestionType.ChoiceDropdown:
		case FormQuestionType.ChoiceDropdownMultiple:
		case FormQuestionType.ChoiceMultiple:
		case FormQuestionType.ChoiceSingle: {
			const type =
				question.type === FormQuestionType.ChoiceCascading
					? FormFieldType.CASCADING_SELECT
					: question.type === FormQuestionType.ChoiceDropdown
						? FormFieldType.SINGLE_SELECT
						: question.type === FormQuestionType.ChoiceDropdownMultiple
							? FormFieldType.MULTIPLE_SELECT
							: question.type === FormQuestionType.ChoiceMultiple
								? FormFieldType.CHECKBOX
								: FormFieldType.RADIO;
			// When apiFormChoices is loaded, linkedChoices contains live choices from the API. When not
			// loaded (e.g. portal-view submitted forms), linkedChoices is undefined, falling back to question.choices.
			const linkedChoices = getLinkedChoices(question, apiFormChoices);
			const options = linkedChoices
				? linkedChoices.map((choice) => convertFormChoice(choice))
				: question.choices
						?.filter((choice) => !choice.other)
						.map((choice) => convertFormChoice(choice)) || [];
			const hasOtherOption = question.choices?.some((choice) => choice.other) || false;
			const value =
				shouldApplyDefaultValues && question.defaultAnswer?.choices
					? getChoiceValue(
							question.defaultAnswer.choices,
							hasOtherOption,
							question,
							type,
							linkedChoices,
							question.defaultAnswer?.text || undefined,
						)
					: fieldData
						? {
								// fieldData contains all the choices in the correct format
								options: fieldData.value.choices || [],
							}
						: formAnswer?.choices
							? getChoiceValue(
									formAnswer.choices,
									hasOtherOption,
									question,
									type,
									// In a submitted (or locked) form, for choice fields (except CMDB fields) that are
									// linked to Jira fields we don't want to get the choice value from the linked Jira
									// field's choice list, but rather from the stored choice list in the submitted
									// form itself.
									// - com.thinktilt.proforma.core.helper.FormTransform.withJiraFieldValues
									// - com.thinktilt.proforma.core.helper.FormTransform.withDataConnectionItems
									// Known caveat:
									// For submitted (or locked) forms in issue-view, to resolve bugs REQIN-1186,
									// JSDCLOUD-13569, and JSDCLOUD-13705, apiFormChoices are loaded with `allowSubmittedForm=true` and displayed instead,
									// meaning live choices from Jira are shown even in submitted state.
									isFormLockedOrSubmitted ? undefined : linkedChoices,
									formAnswer.text || undefined,
								)
							: undefined;

			return {
				type,
				...commonFieldProperties,
				...(hasOtherOption && { hasOtherOption: true }),
				options,
				...(value && { value }),
			};
		}
		case FormQuestionType.Rating: {
			const options = (question.choices ?? [])
				.filter((choice) => !choice.other)
				.map((choice) => convertFormChoice(choice));

			const value: NumberValue | undefined =
				shouldApplyDefaultValues && question.defaultAnswer?.text
					? { number: Number(question.defaultAnswer.text) }
					: fieldData?.value.text
						? { number: Number(fieldData.value.text) }
						: formAnswer?.text
							? { number: Number(formAnswer.text) }
							: undefined;
			return {
				type: FormFieldType.RATING,
				...commonFieldProperties,
				...(value && { value }),
				options,
			};
		}
		case FormQuestionType.Scale: {
			const options = (question.choices ?? [])
				.filter((choice) => !choice.other)
				.map((choice) => convertFormChoice(choice));

			const value: NumberValue | undefined =
				shouldApplyDefaultValues && question.defaultAnswer?.text
					? { number: Number(question.defaultAnswer.text) }
					: fieldData?.value.text
						? { number: Number(fieldData.value.text) }
						: formAnswer?.text
							? { number: Number(formAnswer.text) }
							: undefined;
			return {
				type: FormFieldType.SCALE,
				...commonFieldProperties,
				...(value && { value }),
				options,
			};
		}
		case FormQuestionType.CmdbObject: {
			const type = fieldConfiguration?.multiple
				? FormFieldType.MULTIPLE_SELECT
				: FormFieldType.SINGLE_SELECT;

			let value: OptionsValue | undefined;
			if (isFormLockedOrSubmitted) {
				value =
					formAnswer?.choices &&
					getChoiceValue(
						formAnswer.choices,
						false,
						question,
						type,
						// In a submitted (or locked) form, for CMDB fields, we don't want to get the choice value from
						// the linked Jira CMDB field's choice list, but rather from the stored choice list in the
						// submitted form itself.
						undefined,
						undefined,
					);
			} else {
				value = fieldData?.value.choices && { options: fieldData.value.choices };
			}

			return {
				type,
				...commonFieldProperties,
				options: [],
				...(value && { value }),
			};
		}
		case FormQuestionType.Date: {
			const value: DateValue | undefined =
				shouldApplyDefaultValues && question.defaultAnswer?.date
					? { date: question.defaultAnswer.date }
					: fieldData?.value.date
						? { date: fieldData.value.date }
						: formAnswer?.date
							? { date: formAnswer.date }
							: undefined;
			return {
				type: FormFieldType.DATE,
				...commonFieldProperties,
				...(value && { value }),
			};
		}
		case FormQuestionType.DateTime: {
			const value: DateTimeValue | undefined =
				shouldApplyDefaultValues && (question.defaultAnswer?.date || question.defaultAnswer?.time)
					? {
							date: question.defaultAnswer.date || undefined,
							localTime: question.defaultAnswer.time || undefined,
						}
					: fieldData?.value.date || fieldData?.value.time
						? {
								date: fieldData.value.date || undefined,
								localTime: fieldData.value.time?.match(/^\d+:\d+:\d+$/)
									? fieldData.value.time.replace(/:\d+$/, '')
									: fieldData.value.time,
							}
						: formAnswer?.date || formAnswer?.time
							? {
									date: formAnswer.date || undefined,
									localTime: formAnswer.time || undefined,
								}
							: undefined;
			return {
				type: FormFieldType.DATE_TIME,
				...commonFieldProperties,
				...(value && { value }),
			};
		}
		case FormQuestionType.Time: {
			const value: LocalTimeValue | undefined =
				shouldApplyDefaultValues && question.defaultAnswer?.time
					? { localTime: question.defaultAnswer.time }
					: fieldData?.value.time
						? {
								localTime: fieldData.value.time.match(/^\d+:\d+:\d+$/)
									? fieldData.value.time.replace(/:\d+$/, '')
									: fieldData.value.time,
							}
						: formAnswer?.time
							? { localTime: formAnswer.time }
							: undefined;
			return {
				type: FormFieldType.LOCAL_TIME,
				...commonFieldProperties,
				...(value && { value }),
			};
		}
		case FormQuestionType.Number: {
			const value: NumberValue | undefined =
				shouldApplyDefaultValues && question.defaultAnswer?.text
					? { number: Number(question.defaultAnswer.text) }
					: fieldData?.value.text
						? { number: Number(fieldData.value.text) }
						: formAnswer?.text
							? { number: Number(formAnswer.text) }
							: undefined;
			return {
				type: FormFieldType.NUMBER,
				...commonFieldProperties,
				...(value && { value }),
			};
		}
		case FormQuestionType.RichTextParagraph: {
			const value: AdfValue | undefined =
				shouldApplyDefaultValues && question.defaultAnswer?.adf
					? { adf: question.defaultAnswer.adf }
					: fieldData?.value.adf
						? { adf: fieldData.value.adf }
						: formAnswer?.adf
							? { adf: formAnswer.adf }
							: undefined;
			return {
				type: FormFieldType.RICH_TEXT_PARAGRAPH,
				...commonFieldProperties,
				...(value && { value }),
			};
		}
		case FormQuestionType.UserMultiple:
		case FormQuestionType.UserSingle: {
			const value = fieldData?.value.users
				? getUserChoiceValue(fieldData.value.users)
				: formAnswer?.users
					? getUserChoiceValue(formAnswer.users)
					: undefined;
			return {
				type:
					question.type === FormQuestionType.UserMultiple
						? FormFieldType.MULTIPLE_SELECT
						: FormFieldType.SINGLE_SELECT,
				...commonFieldProperties,
				options: [],
				...(value && { value }),
			};
		}
		case FormQuestionType.TextEmail:
		case FormQuestionType.TextLong:
		case FormQuestionType.TextParagraph:
		case FormQuestionType.TextShort:
		case FormQuestionType.TextUrl:
		default: {
			const type =
				question.type === FormQuestionType.TextEmail
					? FormFieldType.EMAIL
					: question.type === FormQuestionType.TextParagraph
						? FormFieldType.PARAGRAPH
						: question.type === FormQuestionType.TextShort
							? FormFieldType.SHORT_TEXT
							: question.type === FormQuestionType.TextUrl
								? FormFieldType.URL
								: // question.type === FormQuestionType.TextLong || `default`
									FormFieldType.LONG_TEXT;
			const value: TextValue | undefined =
				shouldApplyDefaultValues && question.defaultAnswer?.text
					? { text: question.defaultAnswer.text }
					: fieldData?.value.text
						? { text: fieldData.value.text }
						: formAnswer?.text
							? { text: formAnswer.text }
							: undefined;
			return {
				type,
				...commonFieldProperties,
				...(value && { value }),
			};
		}
	}
};
